import { IReport } from "@mctypes/report.types";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { AppThunk, IRootState } from "../../../store";
import { doGetSingleReportBoard, doUpdateReportBoardGeneralSettings, doUpdateReportData } from "./reportBoardAPI";
import { doGetReportTemplates } from "./reportTemplateAPI";
import { ToastMessagesEnums } from "../../../mc-shared/sharedConstants";
import { apiSlice } from "../../../apiSlice";
import { sortArrayByDate } from "../../utils";
import { IReportTemplate, IReportKeyData, ReportCode } from "../../../mc-shared/reportSchemas/reportSchemas";

export interface IReportReducer {
  currentReportId: string | null;
  reportBoards: IReport.IReportBoardPopulated[];
  reportTemplates: IReportTemplate[];
  filterReportsByLoggedInUserChecked: boolean;
  editedData: IReportKeyData;
}

export const createEmptyEditedData = (): IReportKeyData => ({
  101: null,
  102: null,
  103: null,
  104: null,
  106: null,
  201: null,
  202: null,
  205: null,
  301: null,
  401: null,
  701: null,
  901: null,
});

const initialState: IReportReducer = {
  currentReportId: null,
  reportBoards: null,
  reportTemplates: null,
  filterReportsByLoggedInUserChecked: false,
  editedData: createEmptyEditedData(),
};

export const fetchAndSetSingleReportThunk =
  (reportBoardId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
  };

export const fetchAndSetReportTemplatesThunk = () => async (dispatch) => {
  const reportTemplates = await doGetReportTemplates();
  dispatch(setReportTemplatesInRedux(reportTemplates));
};

export const updateReportSettingsThunk =
  (reportId: string, attr, value): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    await doUpdateReportBoardGeneralSettings(project._id, reportId, attr, value);
    dispatch(fetchAndSetSingleReportThunk(reportId));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.SETTINGS_UPDATED));
  };

export const updateReportDataThunk =
  (reportId: string, message: string, templateId: number): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const editedData = getState().reportReducer.editedData[templateId as ReportCode];
    await doUpdateReportData(project._id, reportId, editedData, message, templateId);
    dispatch(apiSlice.util.invalidateTags(["SingleReportBoard"]));
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.SETTINGS_UPDATED));
  };

export const fetchReportAndUpdateUploadedFilesThunk =
  (reportId: string): AppThunk =>
  async (dispatch: any, getState) => {
    const project = getState().adminReducer.project;
    const report = await doGetSingleReportBoard(project._id, reportId);
  };

// 2) Payload som låser data-typen basert på code
export type SetEditedDataPayload = {
  [C in ReportCode]: { code: C; data: IReportKeyData[C] };
}[ReportCode];
type SetEditedValuePayload = {
  code: ReportCode;
  path: string; // t.ex "generellInfo.utarbeidetAv" eller "items.0.title"
  value: unknown;
};

// reportPath.ts (eller direkt i reportSlice-filen)

export const parsePath = (path: string): Array<string | number> => {
  // Stödjer: a.b[0].c, a[0][1].b, a.0.b osv.
  const normalized = path
    .replace(/\[(\d+)\]/g, ".$1") // [0] -> .0
    .replace(/^\./, ""); // ta bort ev. ledande punkt

  return normalized
    .split(".")
    .filter(Boolean)
    .map((p) => {
      const n = Number(p);
      return Number.isInteger(n) && String(n) === p ? n : p;
    });
};

export const setByPath = (obj: any, path: string, value: unknown) => {
  const parts = parsePath(path);
  if (parts.length === 0) return;

  let cur = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    const nextKey = parts[i + 1]!;
    const nextShouldBeArray = typeof nextKey === "number";

    if (cur[key as any] == null) {
      cur[key as any] = nextShouldBeArray ? [] : {};
    }

    cur = cur[key as any];
  }

  const last = parts[parts.length - 1]!;
  cur[last as any] = value;
};

export const reportSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    resetReportApp: (state) => {
      return initialState;
    },
    setReportTemplatesInRedux: (state, action: PayloadAction<IReportTemplate[]>) => {
      state.reportTemplates = action.payload;
    },
    setEditedDataInRedux: (state, action: PayloadAction<SetEditedDataPayload>) => {
      const { code, data } = action.payload;
      state.editedData[code as any] = data;
    },
    setEditedValueInRedux: (state, action: PayloadAction<SetEditedValuePayload>) => {
      const { code, path, value } = action.payload;
      if (state.editedData[code] == null) state.editedData[code] = {} as any;
      setByPath(state.editedData[code] as any, path, value); // samma setByPath som tidigare
    },
    setCurrentReportIdInRedux: (state, action: PayloadAction<string | null>) => {
      state.currentReportId = action.payload;
    },
    resetEditedDataInRedux: (state) => {
      state.editedData = createEmptyEditedData();
    },
  },
});

export const {
  resetReportApp,
  setReportTemplatesInRedux,
  setCurrentReportIdInRedux,
  setEditedDataInRedux,
  setEditedValueInRedux,
  resetEditedDataInRedux,
} = reportSlice.actions;

export default reportSlice.reducer;

export const getLatestVersionOfReport = (report: IReport.IReportBoardPopulated): IReport.IReportVersion | null => {
  if (!report || !report.versions || report.versions.length === 0) return null;
  const versionesSorted = sortArrayByDate(report.versions, "createdAt", true);
  return versionesSorted[0];
};
